/**
 * Browser-side: register the app service worker, sync session polling, react to SW messages.
 */

import { authService } from '../services/authService';
import { getBaseURL } from '../utils/config';
import logger from '../utils/logger';

let controllerReloadHooked = false;
/** Set only when user activates a waiting worker via activateWaitingServiceWorker(). */
let expectingControllerReload = false;
let controllerReloadInProgress = false;
let pendingAuthPollPayload = null;
let authSyncTimeout = null;
/** Blocks re-sync after SW session invalidation or explicit stop until login. */
let authPollSuppressed = false;
let handlingSessionInvalid = false;
let visibilityAuthPollHooked = false;

const CONTROLLER_RELOAD_TIMEOUT_MS = 30_000;

function hookControllerReload() {
  if (controllerReloadHooked || !('serviceWorker' in navigator)) {
    return;
  }
  controllerReloadHooked = true;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!expectingControllerReload) {
      logger.log(
        '[SW client] controllerchange ignored (iOS restart / reclaim, not user update)'
      );
      return;
    }
    if (controllerReloadInProgress) {
      return;
    }
    controllerReloadInProgress = true;
    expectingControllerReload = false;
    logger.log('[SW client] controllerchange → reload (user activated update)');
    window.location.reload();
  });
}

function postMessageToWorker(message) {
  const worker = navigator.serviceWorker.controller;
  if (worker) {
    worker.postMessage(message);
    logger.log('[SW client] postMessage → controller', message.type);
    return true;
  }
  return false;
}

function flushPendingAuthPollToServiceWorker() {
  if (!('serviceWorker' in navigator) || pendingAuthPollPayload === null) {
    return;
  }

  const message = {
    type: 'CONFIGURE_AUTH_POLL',
    payload: pendingAuthPollPayload,
  };

  if (postMessageToWorker(message)) {
    return;
  }

  navigator.serviceWorker.ready.then(() => {
    if (postMessageToWorker(message)) {
      return;
    }
    const onController = () => {
      if (postMessageToWorker(message)) {
        navigator.serviceWorker.removeEventListener('controllerchange', onController);
        logger.log('[SW client] postMessage → controller (after claim)', message.type);
      }
    };
    navigator.serviceWorker.addEventListener('controllerchange', onController);
    logger.log('[SW client] CONFIGURE_AUTH_POLL waiting for controller');
  });
}

function applyAuthPollSync() {
  if (authPollSuppressed) {
    logger.log('[SW client] auth sync suppressed (session invalid or stopped)');
    return;
  }

  const sessionToken = authService.getActiveSessionToken();
  const apiBaseUrl = authService.baseURL || getBaseURL();

  if (!sessionToken || !apiBaseUrl) {
    logger.log('[SW client] auth sync skipped', {
      hasToken: Boolean(sessionToken),
      hasApiBaseUrl: Boolean(apiBaseUrl),
    });
    pendingAuthPollPayload = null;
    stopAuthPollFromServiceWorker({ suppress: false });
    return;
  }

  pendingAuthPollPayload = {
    sessionToken,
    apiBaseUrl,
    isLinkAuth: Boolean(authService.getLinkSessionToken()),
  };

  logger.log('[SW client] auth sync sending CONFIGURE_AUTH_POLL', {
    isLinkAuth: pendingAuthPollPayload.isLinkAuth,
  });

  flushPendingAuthPollToServiceWorker();
}

/** Debounced — avoids duplicate CONFIGURE_AUTH_POLL from ready + useEffect + register. */
export function syncAuthPollToServiceWorker() {
  if (authPollSuppressed) {
    return;
  }
  clearTimeout(authSyncTimeout);
  authSyncTimeout = setTimeout(applyAuthPollSync, 100);
}

export function clearAuthPollSuppression() {
  authPollSuppressed = false;
  handlingSessionInvalid = false;
}

export function activateWaitingServiceWorker(registration) {
  if (!registration?.waiting) {
    return false;
  }
  expectingControllerReload = true;
  controllerReloadInProgress = false;
  setTimeout(() => {
    if (expectingControllerReload) {
      expectingControllerReload = false;
      logger.log('[SW client] update reload flag cleared (no controllerchange)');
    }
  }, CONTROLLER_RELOAD_TIMEOUT_MS);
  registration.waiting.postMessage({ type: 'SKIP_WAITING' });
  return true;
}

export function registerAppServiceWorker(config) {
  if (process.env.NODE_ENV !== 'production') {
    logger.log('[SW client] Skip register — development mode');
    return;
  }

  if (!('serviceWorker' in navigator)) {
    logger.log('[SW client] Service workers not supported in this browser');
    return;
  }

  const publicUrl = new URL(process.env.PUBLIC_URL, window.location.href);
  if (publicUrl.origin !== window.location.origin) {
    logger.log('[SW client] Skip register — PUBLIC_URL origin mismatch');
    return;
  }

  hookControllerReload();

  window.addEventListener('load', () => {
    const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`;
    logger.log('[SW client] Registering:', swUrl);

    navigator.serviceWorker
      .register(swUrl)
      .then((registration) => {
        logger.log('[SW client] Registered OK, scope=', registration.scope);

        registration.onupdatefound = () => {
          const w = registration.installing;
          if (!w) return;
          w.onstatechange = () => {
            if (w.state !== 'installed') return;
            if (navigator.serviceWorker.controller) {
              logger.log('[SW client] Update installed — use toast / refresh when ready');
              config?.onUpdate?.(registration);
            } else {
              logger.log('[SW client] First install cached');
              config?.onSuccess?.(registration);
            }
          };
        };
      })
      .catch((err) => {
        logger.error('[SW client] register failed:', err);
      });
  });

  navigator.serviceWorker.ready.then(() => {
    logger.log('[SW client] navigator.serviceWorker.ready');
  });
}

export function unregisterAppServiceWorker() {
  navigator.serviceWorker?.ready
    ?.then((r) => r.unregister())
    .catch((err) => logger.error('[SW client] unregister failed:', err));
}

export function stopAuthPollFromServiceWorker({ suppress = true } = {}) {
  clearTimeout(authSyncTimeout);
  authSyncTimeout = null;
  pendingAuthPollPayload = null;
  if (suppress) {
    authPollSuppressed = true;
  }

  const stopMsg = { type: 'STOP_AUTH_POLL' };
  if (!postMessageToWorker(stopMsg)) {
    navigator.serviceWorker.ready.then(() => {
      postMessageToWorker(stopMsg);
    });
  }
}

export function initAppServiceWorkerMessageBridge() {
  if (!('serviceWorker' in navigator)) return;

  navigator.serviceWorker.addEventListener('message', async (event) => {
    const { type, isLinkAuth } = event.data || {};

    if (type === 'SW_VERSION_CHECK') {
      logger.log('[SW client] message SW_VERSION_CHECK → registration.update()');
      try {
        const registration = await navigator.serviceWorker.getRegistration();
        await registration?.update();
      } catch (e) {
        logger.error('[SW client] update failed:', e);
      }
      return;
    }

    if (type === 'SW_SESSION_INVALID') {
      if (handlingSessionInvalid) return;
      handlingSessionInvalid = true;
      authPollSuppressed = true;
      clearTimeout(authSyncTimeout);
      authSyncTimeout = null;
      pendingAuthPollPayload = null;

      logger.log('[SW client] message SW_SESSION_INVALID', { isLinkAuth });

      stopAuthPollFromServiceWorker({ suppress: true });

      if (!window.location.pathname.includes('/signin')) {
        window.location.href = '/signin';
      }
    }
  });

  navigator.serviceWorker.addEventListener('messageerror', (e) => {
    logger.error('[SW client] message error', e);
  });
}

/** Pause auth poll when tab is hidden; resume when visible (per-tab, no cross-tab logout). */
export function initVisibilityAwareAuthPoll() {
  if (visibilityAuthPollHooked || !('serviceWorker' in navigator)) {
    return;
  }
  visibilityAuthPollHooked = true;

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      logger.log('[SW client] tab visible — resuming auth poll');
      syncAuthPollToServiceWorker();
    } else {
      logger.log('[SW client] tab hidden — pausing auth poll');
      stopAuthPollFromServiceWorker({ suppress: false });
    }
  });
}
